import { Alert, Button, Empty, Progress, Space, Tag, Typography } from "antd";

import type { Ruta } from "../../lib/document/paths";
import { agrupar, pendientes, type Sugerencia } from "../../lib/suggestions/types";
import { Comparador } from "./Comparador";

import "./review.css";

const { Text, Title } = Typography;

/**
 * El panel de cambios que la IA propone.
 *
 * Agrupado por sección y en el orden del documento, para poder leerse en
 * paralelo a la hoja de vida. Dentro de cada sección manda la severidad.
 */
export const PanelCambios = ({
  sugerencias,
  seleccionada,
  cargando,
  aviso,
  onSeleccionar,
  onAceptar,
  onRechazar,
  onEditar,
  onAceptarRestantes,
}: {
  sugerencias: Sugerencia[];
  seleccionada: Ruta | null;
  cargando: boolean;
  aviso?: string;
  onSeleccionar: (ruta: Ruta) => void;
  onAceptar: (s: Sugerencia) => void;
  onRechazar: (s: Sugerencia) => void;
  onEditar: (s: Sugerencia, texto: string) => void;
  onAceptarRestantes: () => void;
}) => {
  if (cargando) {
    return (
      <div className="rev-panel">
        <Title level={5}>Revisando tu hoja de vida…</Title>
        <Text type="secondary">
          Estamos buscando qué mejorar. Mientras tanto puedes leerla.
        </Text>
        <Progress percent={100} status="active" showInfo={false} style={{ marginTop: 12 }} />
      </div>
    );
  }

  const sinRevisar = pendientes(sugerencias);
  const revisadas = sugerencias.length - sinRevisar.length;

  // El caso vacío importa más de lo que parece: es la prueba, delante del
  // usuario, de que el sistema no cambia por cambiar.
  if (sugerencias.length === 0) {
    return (
      <div className="rev-panel">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <>
              <Text strong style={{ display: "block", marginBottom: 4 }}>
                Tu hoja de vida llegó en buena forma
              </Text>
              <Text type="secondary">
                No encontramos nada que valga la pena cambiar. Revísala y descárgala.
              </Text>
            </>
          }
        />
      </div>
    );
  }

  return (
    <div className="rev-panel">
      {aviso && <Alert type="warning" showIcon message={aviso} style={{ marginBottom: 12 }} />}

      <div className="rev-progreso">
        <Text strong>
          {sugerencias.length} {sugerencias.length === 1 ? "sugerencia" : "sugerencias"}
          {revisadas > 0 && ` · ${revisadas} revisadas`}
        </Text>
        {sinRevisar.length > 0 && (
          <Button size="small" onClick={onAceptarRestantes}>
            Aceptar las {sinRevisar.length} restantes
          </Button>
        )}
      </div>

      {sugerencias.length > 0 && (
        <Progress
          percent={Math.round((revisadas / sugerencias.length) * 100)}
          size="small"
          showInfo={false}
          strokeColor="var(--marca-morado)"
        />
      )}

      {agrupar(sugerencias).map((grupo) => (
        <section key={grupo.seccion} className="rev-grupo">
          <h3 className="rev-seccion">{grupo.etiqueta}</h3>

          {grupo.sugerencias.map((s) =>
            s.ruta === seleccionada ? (
              <Comparador
                key={s.id}
                sugerencia={s}
                onAceptar={() => onAceptar(s)}
                onRechazar={() => onRechazar(s)}
                onEditar={(texto) => onEditar(s, texto)}
              />
            ) : (
              <button
                key={s.id}
                type="button"
                className={`rev-item ${s.estado !== "pendiente" ? "is-resuelta" : ""}`}
                onClick={() => onSeleccionar(s.ruta)}
              >
                <span className="rev-item-etiqueta">{s.etiqueta}</span>
                <span className="rev-item-razon">{s.razon}</span>
                <Space size={4} style={{ marginTop: 4 }}>
                  {s.severidad === "alta" && s.estado === "pendiente" && (
                    <Tag color="red">Importante</Tag>
                  )}
                  {s.aviso && <Tag>Lo escribes tú</Tag>}
                  {s.estado === "aceptada" && <Tag color="green">Aplicada</Tag>}
                  {s.estado === "editada" && <Tag color="green">Editada</Tag>}
                  {s.estado === "rechazada" && <Tag>Descartada</Tag>}
                </Space>
              </button>
            ),
          )}
        </section>
      ))}
    </div>
  );
};
